import type { DateRange, DayPickerProps } from 'react-day-picker'

import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface DateRangePickerProps {
  id?: string
  selected?: DateRange
  onSelect?: (range: DateRange | undefined) => void
  placeholder?: string
  numberOfMonths?: number
  defaultMonth?: Date
  disabled?: boolean
  disabledDates?: DayPickerProps['disabled']
  startMonth?: Date
  endMonth?: Date
  captionLayout?: DayPickerProps['captionLayout']
  className?: string
  displayFormat?: string
  rangeSeparator?: string
  autoFocus?: boolean
  clearLabel?: string
  closeLabel?: string
  doneLabel?: string
}

export function DateRangePicker({
  id,
  selected,
  onSelect,
  placeholder = 'Select date range',
  numberOfMonths = 2,
  defaultMonth,
  disabled = false,
  disabledDates,
  startMonth,
  endMonth,
  captionLayout = 'dropdown',
  className,
  displayFormat = 'MMM do, yyyy',
  rangeSeparator = ' - ',
  autoFocus = true,
  clearLabel = 'Clear',
  closeLabel = 'Close',
  doneLabel = 'Done',
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)

  const buttonLabel = useMemo(() => {
    if (!selected?.from) {
      return undefined
    }
    const fromLabel = format(selected.from, displayFormat)
    if (selected.to) {
      return `${fromLabel}${rangeSeparator}${format(selected.to, displayFormat)}`
    }
    return fromLabel
  }, [displayFormat, rangeSeparator, selected])

  const hasSelection = Boolean(selected?.from || selected?.to)
  const fallbackMonth = defaultMonth ?? new Date()

  return (
    <Popover open={open} onOpenChange={nextOpen => setOpen(disabled ? false : nextOpen)}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !buttonLabel && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {buttonLabel || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={numberOfMonths}
          defaultMonth={selected?.from ?? fallbackMonth}
          selected={selected}
          onSelect={onSelect}
          disabled={disabledDates}
          captionLayout={captionLayout}
          startMonth={startMonth}
          endMonth={endMonth}
          autoFocus={autoFocus}
          formatters={{
            formatCaption: (date, options) => format(date, displayFormat ?? 'yyyy-MMM-do', options),
          }}
        />
        <div className="flex items-center justify-between border-t px-3 py-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onSelect?.(undefined)}
            disabled={!hasSelection}
          >
            {clearLabel}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setOpen(false)}
            >
              {closeLabel}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setOpen(false)}
            >
              {doneLabel}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
